import { Component, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { InvoiceConfigService, PaymentType, FieldConfig } from './invoice-config.service';
import {ModelToolsProvider} from "../../../../../framework/providers/model-tools/model-tools";
import {ModelProvider} from "../../../../../framework/providers/modelProvider/ModelProvider";

type PaymentView = {
  uid: string;
  fields: FieldConfig[];
};

@Component({
  selector: 'app-new-invoice',
  templateUrl: './new-invoice.component.html',
  styleUrls: ['./new-invoice.component.scss'],
})
export class NewInvoiceComponent implements OnInit {
  form!: FormGroup;

  // snapshots
  config = this.configService.getConfig();
  baseFields = this.configService.getBaseFields();
  paymentTypes: PaymentType[] = this.configService.getPaymentTypes();

  // בחירת סוג תשלום (Reactive)
  paymentTypeCtrl = new FormControl<string | null>(this.paymentTypes[0]?.id ?? null);

  // וקטור יציב מקביל ל-FormArray למניעת איבוד פוקוס
  paymentViews: PaymentView[] = [];

  // אקורדיונים פתוחים (uids)
  expandedValues: string[] = [];

  builtPayload: any = null;

  // סכום רצוי מהנתיב
  desiredAmount: number | null = null;
  docsIds: string | null = null;

  constructor(
    private configService: InvoiceConfigService,
    private route: ActivatedRoute,
    private modelTools:ModelToolsProvider,
    private modelProvider:ModelProvider
  ) {}

  ngOnInit(): void {
    this.form = this.configService.buildBaseFormGroup();
    this.form.addControl('payments', new FormArray([]));

    // params
    const qp = this.route.snapshot.queryParamMap;
    const raw = qp.get('desired') ?? qp.get('target');
    const parsed = raw !== null ? parseFloat(raw) : NaN;
    this.desiredAmount = Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
    this.docsIds = qp.get('docsIds') ?? qp.get('docsIds');
  }

  // ===== helpers =====
  get paymentsFA(): FormArray {
    return this.form.get('payments') as FormArray;
  }

  private makeUid(): string {
    return 'pay_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }

  private fieldsForType(typeId: string): FieldConfig[] {
    const pt = this.configService.getPaymentTypeById(typeId);
    return (pt?.fields ?? []).filter(f => f.visible !== false);
  }

  paymentTypeIdAt(index: number): string | null {
    return (this.paymentsFA.at(index) as FormGroup)?.get('type')?.value ?? null;
  }

  paymentLabelAt(index: number): string {
    const typeId = this.paymentTypeIdAt(index);
    if (!typeId) return '';
    return this.configService.getPaymentTypeById(typeId)?.label ?? typeId;
  }

  amountAt(index: number): number {
    const v = Number((this.paymentsFA.at(index) as FormGroup)?.get('amount')?.value ?? 0);
    return isNaN(v) ? 0 : v;
  }

  totalAmount(): number {
    const fa = this.paymentsFA;
    if (!fa) return 0;
    return fa.controls.reduce((sum, c) => {
      const g = c as FormGroup;
      const amount = Number(g.get('amount')?.value ?? 0);
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);
  }

  progressToDesired(): number | null {
    if (this.desiredAmount === null || this.desiredAmount === 0) return null;
    const pct = (this.totalAmount() / this.desiredAmount) * 100;
    return Math.max(0, Math.min(100, pct));
  }

  dataset(name?: string) {
    return name ? this.configService.getDataset(name) : [];
  }

  getHeaderText(index: number): string {
    const grp = this.paymentsFA.at(index) as FormGroup | undefined;
    const userVal = (grp?.get('title')?.value ?? '').toString().trim();
    if (userVal) return userVal;
    const typeId = this.paymentTypeIdAt(index) || '';
    return this.configService.getPaymentHeaderHintById(typeId) || '';
  }

  // ===== expand helpers =====
  private openCard(uid: string) {
    if (!this.expandedValues.includes(uid)) {
      // יוצרים עותק חדש כדי לטרגר Change Detection
      this.expandedValues = [...this.expandedValues, uid];
    }
  }

  private closeCard(uid: string) {
    if (this.expandedValues.includes(uid)) {
      this.expandedValues = this.expandedValues.filter(x => x !== uid);
    }
  }

  // ===== actions =====
  addPayment(): void {
    const typeId = this.paymentTypeCtrl.value;
    if (!typeId) return;

    const max = this.config.ui.max_payments;
    if (max && this.paymentsFA.length >= max) return;

    const fg = this.configService.buildPaymentFormByTypeId(typeId);
    // ודא שיש title (אם אתה משתמש בשדה כזה)
    if (!fg.get('title')) fg.addControl('title', new FormControl<string>(''));

    this.paymentsFA.push(fg);

    const uid = this.makeUid();
    this.paymentViews.push({
      uid,
      fields: this.fieldsForType(typeId),
    });

    // פתח דיפולטיבי את הכרטיס החדש
    this.openCard(uid);
  }

  removePayment(index: number): void {
    const uid = this.paymentViews[index]?.uid;
    this.paymentsFA.removeAt(index);
    this.paymentViews.splice(index, 1);
    if (uid) this.closeCard(uid);
  }

  clonePayment(index: number): void {
    if (!this.config.ui.allow_clone_payment) return;

    const src = this.paymentsFA.at(index) as FormGroup;
    const typeId = src.get('type')?.value;

    const clone = this.configService.buildPaymentFormByTypeId(typeId);
    if (!clone.get('title')) clone.addControl('title', new FormControl<string>(''));

    Object.keys(clone.controls).forEach((key) => {
      if (key === 'type') return;
      clone.get(key)?.setValue(src.get(key)?.value);
    });

    // מוסיפים לטופס
    this.paymentsFA.insert(index + 1, clone);

    // מוסיפים ל־views עם uid חדש
    const uid = this.makeUid();
    this.paymentViews.splice(index + 1, 0, {
      uid,
      fields: this.fieldsForType(typeId),
    });

    // פתח דיפולטיבי את הכרטיס המשוכפל
    this.openCard(uid);
  }

  build(): void {
    this.builtPayload = this.configService.buildPayload(this.form);
    this.builtPayload['docsIds'] = this.docsIds || null;
    console.log('Built payload:', this.builtPayload);
    this.modelProvider.loading();
    this.modelProvider.customPost('addReceipt', this.builtPayload, {'setPrefix':true}).then((res) => {
        console.log(res)
        this.modelProvider.loadingOff();
        if(res && res['data']['nav'])
            this.modelTools.goTo(res['data']['nav']['page'], res['data']['nav']['params']);
    })
  }

  // ===== trackBy =====
  trackByPayment = (i: number, view: PaymentView) => view.uid;
  trackByField = (i: number, f: FieldConfig) => f.key;

  // מחזיר את הבקר לשדה בתוך התשלום i
  controlAt(index: number, key: string) {
    const grp = this.paymentsFA.at(index) as FormGroup | undefined;
    return grp ? (grp.get(key) as any) : null;
  }

  // Set remaining amount to payment field
  setRestAmount(index: number): void {
    if (this.desiredAmount === null) return;
    const amountControl = this.controlAt(index, 'amount');
    const remaining = this.desiredAmount - this.totalAmount() + (amountControl.value || 0);

    if (amountControl) {
      amountControl.setValue(remaining);
    }
  }
}